import { ApolloError } from '@apollo/client';
import { ExclamationCircleIcon } from '@heroicons/react/16/solid';
import { format, parseISO } from 'date-fns';
import { Link } from 'hotel/navigation';

import ErrorMessage from 'components/ErrorMessage';
import { Profile, RosterRequest, User } from 'generated/graphql';
import buildUserDisplayName from 'utilities/buildUserDisplayName';
import { pluralize } from 'utilities/pluralize';

type Props = {
  duplicateRequests: Array<
    Pick<RosterRequest, 'id' | 'createdAt'> & {
      requestedBy?: {
        profile?: Pick<Profile, 'firstName' | 'lastName'> | null;
        username: User['username'];
      } | null;
    }
  >;
  error?: ApolloError;
  type?: 'guests' | 'rosterItems';
};

export default function DuplicateChangeRequestWarning({
  duplicateRequests,
  error,
  type = 'rosterItems',
}: Props) {
  if (error) {
    return (
      <>
        <ErrorMessage errorMessage="Failed to check for duplicate requests" />
      </>
    );
  }
  return (
    <div className="rounded-md border bg-yellow-50 p-4">
      <div className="flex self-start">
        <div className="shrink-0">
          <ExclamationCircleIcon className="size-5 text-yellow-500" />
        </div>
        <div className="ml-3">
          <h3 className="mb-2 font-medium text-yellow-700">
            {type === 'rosterItems' && (
              <>
                There {duplicateRequests.length === 1 ? 'is' : 'are'}{' '}
                {duplicateRequests.length} open change{' '}
                {pluralize(duplicateRequests.length, 'request')} for some of
                these roster entries
              </>
            )}
            {type === 'guests' && (
              <>
                There {duplicateRequests.length === 1 ? 'is' : 'are'}{' '}
                {duplicateRequests.length} open New Reservation{' '}
                {pluralize(duplicateRequests.length, 'request')} for some of
                these guests
              </>
            )}
          </h3>
          <ul className="list-inside list-disc text-sm font-medium text-yellow-700">
            {duplicateRequests.map((duplicateRequest) => {
              const requestedByName = buildUserDisplayName(
                duplicateRequest.requestedBy,
              );

              return (
                <li key={duplicateRequest.id}>
                  Request #{duplicateRequest.id} requested by {requestedByName}{' '}
                  on {format(parseISO(duplicateRequest.createdAt), 'M/d/yyyy')}
                  <Link
                    to={`/requests/${duplicateRequest.id}`}
                    className="ml-2 text-secondaryBlue underline"
                  >
                    View this request
                  </Link>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </div>
  );
}
